import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
// --- gsap ----------------------------------------------------------------
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
// --- lottie ----------------------------------------------------------------
import Lottie from "lottie-react";
import heroLottie from "../assets/lotties/logo.json";
import contactLogo from "../assets/lotties/logo-neon.json";
// --- media ----------------------------------------------------------------
import heroVideo from "../assets/videos/hero-banner.mp4";
import heroVideoMo from "../assets/videos/hero-banner-mo.mp4";
import {
  mainList01,
  mainList02,
  mainList03,
  mainList04,
  mainList05,
  mainList06,
  arrowIconRight,
  kakaoIcon,
  // mapImg,
  arrowIconPrev,
  arrowIconNext,
  logoFavicon,
} from "../assets/images";
// --- style ----------------------------------------------------------------
import "../styles/pages/Home.scss";

gsap.registerPlugin(ScrollTrigger);

function Home() {
  // --- 상태 ----------------------------------------------------------------
  const [current, setCurrent] = useState(0);
  const [isMobileSlider, setIsMobileSlider] = useState(false);
  // 애니메이션
  const scopeRef = useRef(null);
  // 지도
  const mapRef = useRef(null);

  useEffect(() => {
    // 네이버 지도 API 스크립트가 로드되었는지 확인
    const initMap = () => {
      if (typeof window !== "undefined" && window.naver && window.naver.maps) {
        const mapOptions = {
          center: new window.naver.maps.LatLng(35.872521, 128.595505),
          zoom: 16, //지도의 초기 줌 레벨
          minZoom: 10, //지도의 최소 줌 레벨
        };

        // Map을 먼저 생성
        const map = new window.naver.maps.Map("map", mapOptions);
        mapRef.current = map;

        // 생성된 map 객체를 사용해서 Marker 생성
        const marker = new window.naver.maps.Marker({
          position: new window.naver.maps.LatLng(35.872521, 128.595505),
          map: map,
        });

        // InfoWindow 내용 생성
        const contentString = [
          "<style>",
          '  .iw_inner { font-family: "Pretendard", "Gmarket Sans", system-ui, sans-serif; padding: 12px 16px; display: flex; align-items: flex-start; gap: 8px;}',
          "  .iw_inner img { width: 30px; height: 30px; flex-shrink: 0; }",
          "  .iw_inner .iw_content { flex: 1; display: flex; flex-direction: column; justify-content: space-between; height: 30px; }",
          '  .iw_inner h3 { font-family: "Gmarket Sans", "Pretendard", system-ui, sans-serif; font-weight: 700; font-size: 18px; margin: 0; color: #111; }',
          '  .iw_inner p { font-family: "Pretendard", system-ui, sans-serif; font-weight: 400; font-size: 14px; margin: 0; color: #4F4F4F; line-height: 1.6; }',
          "</style>",
          '<div class="iw_inner">',
          '   <img src="' + logoFavicon + '" alt="MOB STUDIO" />',
          '   <div class="iw_content">',
          "     <h3>MOB STUDIO</h3>",
          "     <p>대구 중구 동성로1가 33 2층 MOB 스튜디오</p>",
          "   </div>",
          "</div>",
        ].join("");

        // InfoWindow 생성
        const infowindow = new window.naver.maps.InfoWindow({
          content: contentString,
          borderColor: "rgba(0, 0, 0, 0.3)",
          boxShadow: "3px 2px 8px rgba(0, 0, 0, 0.2)",
          anchorSize: new window.naver.maps.Size(20, 20),
          anchorSkew: true,
          zIndex: 8,
        });

        // InfoWindow borderRadius 적용 함수
        const applyBorderRadius = () => {
          setTimeout(() => {
            const mapElement = document.getElementById("map");
            if (mapElement) {
              // InfoWindow 컨테이너 찾기 (배경색이 있는 div)
              const infoWindows = mapElement.querySelectorAll('div[style*="background"]');
              infoWindows.forEach((el) => {
                const bg = el.style.background || window.getComputedStyle(el).background;
                if (bg && bg.includes("rgb") && !bg.includes("transparent")) {
                  el.style.borderRadius = "10px";
                  // overflow: hidden 제거 - anchor(화살표)가 잘리지 않도록
                }
              });
            }
          }, 50);
        };

        // InfoWindow 열림 이벤트 리스너
        window.naver.maps.Event.addListener(infowindow, "domready", function () {
          applyBorderRadius();
        });

        // 마커 클릭 이벤트 리스너
        window.naver.maps.Event.addListener(marker, "click", function (e) {
          if (infowindow.getMap()) {
            infowindow.close();
          } else {
            infowindow.open(map, marker);
          }
        });

        // 초기 InfoWindow 열기
        infowindow.open(map, marker);
        applyBorderRadius();
      } else {
        // 스크립트가 아직 로드되지 않았다면 잠시 후 다시 시도
        setTimeout(initMap, 100);
      }
    };

    initMap();

    // cleanup 함수
    return () => {
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, []);

  // dark background 처리
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    html.classList.add("is-home");
    body.classList.add("is-home");

    return () => {
      html.classList.remove("is-home");
      body.classList.remove("is-home");
    };
  }, []);

  // animation
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // 모바일 기준
      if (isMobileSlider) {
        gsap.fromTo(
          ".space-list",
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out", overwrite: true }
        );
        return;
      }
      // 데스트탑 기준
      const items = gsap.utils.toArray(".space-list > li");

      // 초기 숨김
      gsap.set(items, { autoAlpha: 0, y: 40 });

      ScrollTrigger.batch(items, {
        start: "top 75%",
        once: true,

        onEnter: (batch) => {
          gsap.to(batch, {
            autoAlpha: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            stagger: 0.18,
            overwrite: true,
          });
        },
      });

      // 이미지 많으면 초기 refresh 타이밍 때문에 엇갈릴 수 있어서 한 번 더
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }, scopeRef);

    return () => ctx.revert();
  }, []);

  // 모바일 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobileSlider(window.innerWidth <= 767);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // --- 데이터 ----------------------------------------------------------------
  const spaceData = [
    {
      id: 1,
      image: mainList01,
      quote: "누구나 아티스트가 되는 공간.\n오늘, MOB 라운지에서 당신의 멜로디를 남겨보세요.",
      place: "MOB 체험 카페 라운지",
      desc: "음악이 머무는 공간, MOB케험 카페\n단 하나의 멜로디가 당신의 하루를 바꿉니다.\n뮤지션들이 영감을 마시는 공간, MOB Lounge",
    },
    {
      id: 2,
      image: mainList02,
      quote: "Music on Beyond. All Makers One Stage\n그 한마디가 음악이 되는 순간",
      place: "MOB 녹음실",
      desc: "이곳은 단순한 녹음실이 아닙니다.\n감정, 숨결 그리고 기술이 하나로 섞이는 음악의 첫 무대\n당신의 한 소절이 새로운 장르를 만들고,\n한번의 녹음이 세상의 감정을 바꿉니다.\n음악의 경계를 넘어, 모든 창작자가 한 무대 위로.",
    },
    {
      id: 3,
      image: mainList03,
      quote: "누구나 자신만의 이야기를 세상에 전할 수 있는 곳.\n조명 하나, 카메라 하나로도 완벽한 무대가 완성됩니다",
      place: "MOB 개인 스튜디오",
      desc: "Music on Beyond.\n우리는 단순한 방송이 아닌, 콘텐츠를 통해\n 새로운 문화의 파도를 만듭니다.\n당신이 말하는 순간, 이곳은 이미 무대입니다.",
    },
    {
      id: 4,
      image: mainList04,
      quote: "카메라가 켜지는 순간,\n당신의 표정과 음악은 하나의 무대가 됩니다.",
      place: "MOB 프로필 · 영상 촬영 스튜디오",
      desc: "프로필 사진, 뮤직 비디오, 라이브 클립, 숏폼 -\n당신의 음악과 얼굴을 동시에 담는 공간",
    },
    {
      id: 5,
      image: mainList05,
      quote: "오늘의 배움이 미래의 무대가 되는 곳",
      place: "MOB 레슨 룸",
      desc: "단순한 레슨이 아닌, 데뷔로 향하는 리허설입니다.\n보컬, 작사, 댄스, DJ, 프로듀싱, 튠믹싱마스터링,\n AI 작곡, AI영상 퍼포먼스\n모두가 성장하고 연결되는 창작의 허브.\n한 사람의 연습이 또 다른 사람의 영감이 되고,\n우리는 함께 음악의 경계를 넘어갑니다.",
    },
    {
      id: 6,
      image: mainList06,
      quote: "완벽은 사운드의 디테일에 있다.\n이곳에서 음악은 완성되고, 새로운 무대로 나아간다.",
      place: "MOB 컨트롤 룸",
      desc: "감정의 온도를 정밀하게 조율하는 공간,\n믹싱 · 마스터링 · 보컬 튜닝이 교차하는 MOB의 심장부.\n엔지니어, 보컬, 프로듀서, 크리에이터 -\n모두의 손끝에서 음악은 완성되고,\n새로운 생명을 얻습니다.\n혼자가 아니라 함께 만들어지는 무대입니다.",
    },
  ];

  // --- 슬라이더 ----------------------------------------------------------------
  const total = spaceData.length;
  // 가운데/왼쪽/오른쪽 포지션 클래스
  const getPositionClass = (index) => {
    if (!isMobileSlider) return "";

    if (index === current) return "is-center";

    const prevIndex = (current - 1 + total) % total;
    const nextIndex = (current + 1) % total;

    if (index === prevIndex) return "is-left";
    if (index === nextIndex) return "is-right";

    return "is-hidden";
  };

  const handleNext = () => {
    setCurrent((prev) => (prev + 1) % total);
  };

  const handlePrev = () => {
    setCurrent((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  return (
    <main className="home page-wrapper" ref={scopeRef}>
      {/* === hero section === */}
      <div className="home__hero">
        {/* PC video */}
        <video
          className="hero-video hero-video--pc"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>

        {/* Mobile video */}
        <video
          className="hero-video hero-video--mo"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src={heroVideoMo} type="video/mp4" />
        </video>

        {/* overlay */}
        <div className="hero-overlay" aria-hidden="true"></div>

        {/* logo lottie */}
        <Lottie animationData={heroLottie} loop autoplay className="hero-lottie" />
      </div>

      {/* === main copy === */}
      <div className="home__copy">
        <p>
          <small>음악의 경계를 넘어 새로운 창작의 시대를 엽니다</small>
          <strong className="gfont">MUSIC ON BEYOND</strong>
        </p>
        <p>
          <strong className="gfont">ALL MAKERS, ONE STAGE</strong>
          <small>뮤지션, 크리에이터, 셀럽 - <span className="spacing">모두가 한 무대에서 하나의 작품이 됩니다.</span></small>
        </p>

        <div className="copy-desc">
          <p>
            누구나 음악으로 표현할 수 있는 시대, <span>그 시작은 <b>MOB STUDIO에서 </b></span>
          </p>
          <p>
            <b>당신의 영감이 곡이 되는 순간,</b> <span>MOB 스튜디오가 함께합니다.</span>
          </p>
        </div>
      </div>

      {/* === main content === */}
      <section className="home__content">
        {/* --- our space --- */}
        <div className="home__space">
          <h2 className="content-tit">OUR SPACE</h2>

          {isMobileSlider ? (
            // ~ 767px
            <div className="space-slider">
              <div className="space-slider__slide">
                <div className="slide-controller" role="group" aria-label="Our Space image slider controls">
                  <button type="button" onClick={handlePrev} aria-label="Previous" className="arrow arrow--prev">
                    <img src={arrowIconPrev} alt="" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={handleNext} aria-label="Next" className="arrow arrow--next">
                    <img src={arrowIconNext} alt="" aria-hidden="true" />
                  </button>
                </div>

                {/* 이미지 슬라이더 */}
                <ul className="slide-list">
                  {spaceData.map((item, idx) => (
                    <li
                      key={item.id}
                      className={getPositionClass(idx)}
                      aria-current={idx === current ? "true" : undefined}
                    >
                      <img src={item.image} alt={`View of ${item.place}`} />
                    </li>
                  ))}
                </ul>
              </div>

              <dl className="space-slider__desc" aria-live="polite">
                <dt>"{spaceData[current].quote}"</dt>
                <dd>
                  <strong>{spaceData[current].place}</strong>
                  <p>{spaceData[current].desc}</p>
                </dd>
              </dl>
            </div>
          ) : (
            // 768px ~ desktop
            <ul className="space-list">
              {spaceData.map((item, idx) => (
                <li key={item.id} className={getPositionClass(idx)}>
                  <figure className="space-list__com">
                    <img src={item.image} alt={`View of ${item.place}`} />
                    <figcaption>
                      <dl>
                        <dt>"{item.quote}"</dt>
                        <dd>
                          <strong>{item.place}</strong>
                          <p>{item.desc}</p>
                        </dd>
                      </dl>
                    </figcaption>
                  </figure>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* --- MOB Service --- */}
        <div className="home__service">
          <small>About Service</small>
          <p>
            <span>작곡부터 녹음까지, 한곳에서.</span>
            <span>MOB STUDIO에서 당신의 사운드를 완성하세요.</span>
          </p>
          <Link to="/service">
            MOB 서비스 바로가기
            <img src={arrowIconRight} alt="" aria-hidden="true" />
          </Link>
        </div>

        {/* --- MOB Studio --- */}
        <div className="home__map">
          <h2 className="content-tit">MOB 스튜디오 오시는 길</h2>
          {/* (임시) -> api 적용 후, 스타일 적용 1200x500 기준 */}
          <div id="map" className="map" style={{ width: "100%", height: "500px" }}></div>
        </div>

        {/* --- contact us --- */}
        <div className="home__contact">
          <p>
            지금 당신의 무대를 시작하세요 <b>All makers, one stage</b>
          </p>
          <h2 className="gfont">CONTACT US</h2>
          <Lottie animationData={contactLogo} loop autoplay className="contact-lottie" />

          <div className="kakao-link">
            <span>지금 시작해 보세요.</span>
            <a href="https://pf.kakao.com/_QLTxgn" target="_blank" rel="noopener noreferrer">
              <img src={kakaoIcon} alt="" aria-hidden="true" />
              카카오 채널 문의
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
